// Compiles a hand-written SongDef (§13.4) into per-bar note and drum events, and derives the
// adaptive layers that are computed from the harmony rather than written out: Golden brass stabs,
// the heroic guide-tone countermelody, the SD sparkle, the MATCH POINT pad and the double-time
// kit. Pure and DOM-free: the sequencer only transposes and schedules what this produces.
import type { DrumId, DrumPatterns, InstrumentId, SongDef } from './songTypes';
import { chordDegree, isNoteName, noteToMidi, parseRoman, voiceChord, type ChordSpec } from './theory';

export const STEPS_PER_BAR = 16;
export const SONG_BARS = 16;

export interface NoteEv { step: number; midi: number; dur: number; vel: number; /** Velocity written with '!' or '?'. */ marked?: boolean }
export interface DrumEv { step: number; drum: DrumId; vel: number }

export interface CompiledBar {
  index: number;
  chord: ChordSpec;
  voicing: number[];
  kick: DrumEv[]; snare: DrumEv[]; hat: DrumEv[]; perc: DrumEv[];
  bass: NoteEv[]; chords: NoteEv[]; lead: NoteEv[]; arp: NoteEv[];
  /** Derived layers. */
  stabs: NoteEv[]; counter: NoteEv[]; sparkle: NoteEv[]; pad: NoteEv[];
  fill: boolean;
}

export interface CompiledSong {
  def: SongDef;
  /** MIDI of the tonic in the written key (octave of `rootMidi`). */
  tonic: number;
  bars: CompiledBar[];
  /** Intro bars before tick 0: [bar −2, bar −1]. */
  intro: { hat: DrumEv[]; kick: DrumEv[]; snare: DrumEv[]; perc: DrumEv[]; bass: NoteEv[] }[];
  /** Double-time kit for "≤ 2 alive". */
  doubleTime: { kick: DrumEv[]; snare: DrumEv[]; hat: DrumEv[] };
  /** MATCH POINT heartbeat (two hits per bar). */
  heartbeat: DrumEv[];
  /** The drum fill of a transition bar (the bar before a phase entry): the song's fill. */
  transition: { kick: DrumEv[]; snare: DrumEv[]; hat: DrumEv[]; perc: DrumEv[] };
}

const DRUM_VEL: Readonly<Record<string, number>> = { x: 0.78, X: 1, o: 0.42, O: 0.8, r: 0.6 };

/** Parses a 16-step drum string into events (an 'r' is a two-stroke roll inside the step). */
export function parseDrums(pattern: string, drum: DrumId, openDrum?: DrumId): DrumEv[] {
  if (pattern.length !== STEPS_PER_BAR) throw new Error(`drum pattern '${pattern}' is not 16 steps`);
  const out: DrumEv[] = [];
  for (let s = 0; s < STEPS_PER_BAR; s++) {
    const ch = pattern[s];
    if (ch === '.') continue;
    const vel = DRUM_VEL[ch];
    if (vel === undefined) throw new Error(`bad drum step '${ch}' in '${pattern}'`);
    const id = ch === 'O' && openDrum ? openDrum : drum;
    if (ch === 'r') {
      out.push({ step: s, drum: id, vel });
      out.push({ step: s + 0.5, drum: id, vel: vel * 0.8 });
    } else {
      out.push({ step: s, drum: id, vel });
    }
  }
  return out;
}

export function tokens(line: string): string[] {
  const t = line.trim().split(/\s+/);
  if (t.length !== STEPS_PER_BAR) throw new Error(`line '${line}' has ${t.length} steps, expected 16`);
  return t;
}

/** Velocity marks on a note token: '!' accent, '?' soft. */
function noteToken(tok: string): { midi: number; vel: number; marked: boolean } {
  let vel = 0.8;
  let name = tok;
  let marked = false;
  if (name.endsWith('!')) {
    vel = 1;
    marked = true;
    name = name.slice(0, -1);
  } else if (name.endsWith('?')) {
    vel = 0.55;
    marked = true;
    name = name.slice(0, -1);
  }
  if (!isNoteName(name)) throw new Error(`bad note token '${tok}'`);
  return { midi: noteToMidi(name), vel, marked };
}

/**
 * Parses melodic bars ('C3 - - . E3 …'); a '-' extends the previous note, also across a bar
 * line, so ties work. Returns one event list per bar.
 */
export function parseLines(lines: readonly string[]): NoteEv[][] {
  const bars: NoteEv[][] = [];
  let last: NoteEv | null = null;
  for (const line of lines) {
    const evs: NoteEv[] = [];
    const t = tokens(line);
    for (let s = 0; s < STEPS_PER_BAR; s++) {
      const tok = t[s];
      if (tok === '.') {
        last = null;
      } else if (tok === '-') {
        if (last) last.dur += 1;
      } else {
        const { midi, vel, marked } = noteToken(tok);
        last = marked ? { step: s, midi, dur: 1, vel, marked } : { step: s, midi, dur: 1, vel };
        evs.push(last);
      }
    }
    bars.push(evs);
  }
  return bars;
}

/** Metric weight of a 16th step: downbeat, beat 3, beats 2 and 4, other 8ths, off-16ths. */
function metric(step: number): number {
  const s = Math.round(step) % STEPS_PER_BAR;
  if (s === 0) return 1.12;
  if (s === 8) return 1.06;
  if (s % 4 === 0) return 1;
  return s % 2 === 0 ? 0.94 : 0.88;
}

/**
 * Phrasing for the notes written without a velocity mark: metric accents (strong beats lean in,
 * off-16ths are lighter), long notes a little fuller, and the peak of each 4-bar phrase (its
 * highest note) lifted, so a melody breathes like it is played. `depth` scales it (the bass is
 * steadier than the lead). The average stays near the unmarked 0.8.
 */
export function phrase(bars: NoteEv[][], depth = 1): void {
  const shaped: { n: NoteEv; base: number; w: number }[] = [];
  for (let p = 0; p < bars.length; p += 4) {
    const notes = bars.slice(p, p + 4).flat();
    const peak = Math.max(...notes.map((n) => n.midi));
    for (const n of notes) {
      if (n.marked) continue;
      let w = metric(n.step);
      if (n.dur >= 4) w *= 1.04;
      if (n.midi === peak) w *= 1.08;
      shaped.push({ n, base: n.vel, w: 1 + (w - 1) * depth });
    }
  }
  // Keep the line's power (the sampler plays vel^1.6) where it was balanced.
  const before = shaped.reduce((sum, x) => sum + x.base ** 1.6, 0);
  const after = shaped.reduce((sum, x) => sum + (x.base * x.w) ** 1.6, 0);
  const k = after > 0 ? (before / after) ** (1 / 1.6) : 1;
  for (const x of shaped) x.n.vel = Math.min(1, x.base * x.w * k);
}

/** Chord-rhythm string → [step, durSteps, vel] hits. */
export function parseComp(comp: string): { step: number; dur: number; vel: number }[] {
  if (comp.length !== STEPS_PER_BAR) throw new Error(`comp '${comp}' is not 16 steps`);
  const hits: { step: number; dur: number; vel: number }[] = [];
  for (let s = 0; s < STEPS_PER_BAR; s++) {
    const ch = comp[s];
    if (ch === 'x' || ch === 'X' || ch === 'o') hits.push({ step: s, dur: 1, vel: ch === 'X' ? 0.95 : ch === 'o' ? 0.5 : 0.75 });
    else if (ch === '-' && hits.length > 0) hits[hits.length - 1].dur += 1;
    else if (ch !== '.' && ch !== '-') throw new Error(`bad comp step '${ch}' in '${comp}'`);
  }
  return hits;
}

function arpTokens(arp: string): (number | '.' | '-')[] {
  return tokens(arp).map((t) => (t === '.' || t === '-' ? t : Number(t)));
}

const DEFAULT_STABS = 'X.....x.........';
const SPARKLE = '8 12 10 12 15 12 10 12 8 12 10 12 15 12 10 12';
const DOUBLE_TIME = { kick: 'x.x.x.x.x.x.x.x.', snare: '....X.......X..x', hat: 'XxxxXxxxXxxxXxxx' };
/** Transition fill for songs without their own. */
const DEFAULT_FILL: DrumPatterns = { kick: 'x.......x.......', snare: '........x.o.xrXX', hat: 'x.x.x.x.x.......' };

function drumsOf(p: DrumPatterns, def: SongDef): Pick<CompiledBar, 'kick' | 'snare' | 'hat' | 'perc'> {
  return {
    kick: parseDrums(p.kick, def.kit.kick),
    snare: parseDrums(p.snare, def.kit.snare),
    hat: parseDrums(p.hat, def.kit.hat, def.kit.hatOpen),
    perc: p.perc && def.instruments.perc ? parseDrums(p.perc, def.instruments.perc) : [],
  };
}

/** Guide-tone countermelody: the 3rd on beat 1 and the 7th (or 5th) on beat 3, voice-led. */
function guideTones(chord: ChordSpec, tonic: number, prev: number | null): NoteEv[] {
  const third = chordDegree(chord, tonic, 3);
  const seventhOrFifth = chord.intervals.length > 3 ? chordDegree(chord, tonic, 7) : chordDegree(chord, tonic, 5);
  const place = (m: number, near: number): number => {
    let best = m;
    for (let k = -3; k <= 3; k++) if (Math.abs(m + 12 * k - near) < Math.abs(best - near)) best = m + 12 * k;
    return Math.max(62, Math.min(81, best));
  };
  const a = place(third, prev ?? 70);
  const b = place(seventhOrFifth, a);
  return [{ step: 0, midi: a, dur: 7, vel: 0.85 }, { step: 8, midi: b, dur: 4, vel: 0.75 }, { step: 12, midi: place(chordDegree(chord, tonic, 5), b), dur: 3, vel: 0.6 }];
}

export function compileSong(def: SongDef): CompiledSong {
  if (def.chords.length !== SONG_BARS || def.bass.length !== SONG_BARS || def.lead.length !== SONG_BARS) {
    throw new Error(`song ${def.id}: chords, bass and lead need 16 bars`);
  }
  const tonic = def.rootMidi;
  const chords = def.chords.map((c) => parseRoman(c, def.scale));
  const bass = parseLines(def.bass);
  const lead = parseLines(def.lead);
  phrase(lead, 1);
  phrase(bass, 0.6);
  const [lo, hi] = def.chordRange ?? [55, 76];
  const voicingStyle = def.voicing ?? 'close';
  const compA = parseComp(def.comp ?? 'x---------------');
  const compB = def.compB ? parseComp(def.compB) : compA;
  const arp = def.arp ? arpTokens(def.arp) : null;
  const sparkle = arpTokens(SPARKLE);
  const stabs = parseComp(def.stabs ?? DEFAULT_STABS);

  let prevVoicing: number[] | null = null;
  let prevStab: number[] | null = null;
  let prevPad: number[] | null = null;
  let prevGuide: number | null = null;
  const bars: CompiledBar[] = [];
  for (let i = 0; i < SONG_BARS; i++) {
    const chord = chords[i];
    const voicing = voiceChord(chord, tonic, voicingStyle, lo, hi, prevVoicing);
    prevVoicing = voicing;
    const isB = i >= 8;
    const fill = def.fill !== undefined && (i === 7 || i === 15);
    const groove = fill && def.fill ? def.fill : isB && def.drumsB ? def.drumsB : def.drums;
    const comp = isB ? compB : compA;
    const chordEvs: NoteEv[] = [];
    for (const h of comp) for (const m of voicing) chordEvs.push({ step: h.step, midi: m, dur: h.dur, vel: h.vel });

    const arpEvs: NoteEv[] = [];
    if (arp) {
      // The arp sits an octave around C5 regardless of the chord's own voicing.
      const base = tonic + chord.root + 12 * Math.round((72 - (tonic + chord.root)) / 12) - 12 + 12 * (def.arpOctave ?? 0);
      let lastArp: NoteEv | null = null;
      arp.forEach((tok, s) => {
        if (tok === '.') lastArp = null;
        else if (tok === '-') {
          if (lastArp) lastArp.dur++;
        } else {
          lastArp = { step: s, midi: chordDegree(chord, base - chord.root, tok), dur: 1, vel: s % 4 === 0 ? 0.75 : 0.55 };
          arpEvs.push(lastArp);
        }
      });
    }

    const stabVoicing = voiceChord(chord, tonic, 'close', 60, 77, prevStab);
    prevStab = stabVoicing;
    const stabEvs: NoteEv[] = [];
    for (const h of stabs) for (const m of stabVoicing) stabEvs.push({ step: h.step, midi: m, dur: Math.min(h.dur, 2), vel: h.vel });

    const counter = guideTones(chord, tonic, prevGuide);
    prevGuide = counter[1].midi;

    const sparkleBase = tonic + chord.root + 12 * Math.round((76 - (tonic + chord.root)) / 12);
    const sparkleEvs: NoteEv[] = [];
    sparkle.forEach((tok, s) => {
      if (typeof tok === 'number') sparkleEvs.push({ step: s, midi: chordDegree(chord, sparkleBase - chord.root, tok) - 12, dur: 1, vel: s % 4 === 0 ? 0.6 : 0.38 });
    });

    const padVoicing = voiceChord(chord, tonic, 'spread', 48, 74, prevPad);
    prevPad = padVoicing;

    bars.push({
      index: i, chord, voicing,
      ...drumsOf(groove, def),
      bass: bass[i], chords: chordEvs, lead: lead[i], arp: arpEvs,
      stabs: stabEvs, counter, sparkle: sparkleEvs,
      pad: padVoicing.map((m) => ({ step: 0, midi: m, dur: 16, vel: 0.6 })),
      fill,
    });
  }

  const pickup = def.pickup ?? { kick: '................', snare: '........o.o.xrXX', hat: 'x...x...x...x...' };
  const lastBass = bass[SONG_BARS - 1];
  const intro = [
    { hat: parseDrums('o...o...o...o...', def.kit.hat), kick: [], snare: [], perc: [], bass: [] },
    { ...drumsOf(pickup, def), bass: lastBass },
  ];
  return {
    def, tonic, bars, intro,
    doubleTime: {
      kick: parseDrums(DOUBLE_TIME.kick, def.kit.kick),
      snare: parseDrums(DOUBLE_TIME.snare, def.kit.snare),
      hat: parseDrums(DOUBLE_TIME.hat, def.kit.hat),
    },
    heartbeat: [{ step: 0, drum: 'heartbeat', vel: 1 }, { step: 3, drum: 'heartbeat', vel: 0.7 }],
    transition: drumsOf(def.fill ?? DEFAULT_FILL, def),
  };
}

/** Every instrument a song needs rendered (pad is live). */
export function songInstruments(def: SongDef): InstrumentId[] {
  const set = new Set<InstrumentId>([def.instruments.bass, def.instruments.chords, def.instruments.lead, 'brass', 'bell', def.counter ?? 'brass']);
  if (def.instruments.arp) set.add(def.instruments.arp);
  set.delete('pad');
  return [...set];
}

/** Every drum a song needs rendered. */
export function songDrums(def: SongDef): DrumId[] {
  const set = new Set<DrumId>([def.kit.kick, def.kit.snare, def.kit.hat, 'heartbeat', 'crash', 'riser']);
  if (def.kit.hatOpen) set.add(def.kit.hatOpen);
  if (def.kit.crash) set.add(def.kit.crash);
  if (def.instruments.perc) set.add(def.instruments.perc);
  return [...set];
}
